// Editing commands shared by toolbar, menus, command palette, shortcuts and field list.
import { addField, removeField, duplicateField, moveField, updateField, getField } from '../itf/label.js';
import { S, mutate, select, selectedFields, editableSelection, singleSelected, isLocked, setLocked, emit } from './state.js';
import { toast } from './dom.js';
import { fieldBounds, unionBounds, localBox, intersects } from './geometry.js';
import { ensureBwip } from './bwip.js';

const KIND_NAMES = { text: 'text', date: 'date', barcode: 'barcode' };

/** mutate() that reports a LabelError (nothing is written) instead of throwing. */
export function tryMutate(fn, opts) {
  try {
    return mutate(fn, opts);
  } catch (err) {
    console.error(err);
    toast(err.message || String(err), 'bad');
    return null;
  }
}

// ----- placement of new fields ----------------------------------------------------------------
const MARGIN = 500; // keep new fields 5 mm inside the label
const ROW_GAP = 150; // 1.5 mm between rows
const COL_STEP = 250;
const CLEARANCE = 75;
let lastAdded = null;

/**
 * First free slot for a box of size box.w × box.h: prefer directly below `anchor` (the selected field),
 * else scan down in rows (height + 1.5 mm), then right. Returns label-space bounds or null.
 */
export function findFreeSlot(labelW, labelH, box, others, anchor = null) {
  const wide = box.w > labelW - 2 * MARGIN;
  const inside = (r) => r.x >= MARGIN && r.y >= MARGIN && r.y + r.h <= labelH - MARGIN && (wide || r.x + r.w <= labelW - MARGIN);
  const free = (r) => !others.some((o) => intersects({ x: r.x - CLEARANCE, y: r.y - CLEARANCE, w: r.w + 2 * CLEARANCE, h: r.h + 2 * CLEARANCE }, o));
  if (anchor) {
    const r = { x: Math.max(MARGIN, Math.round(anchor.x)), y: Math.round(anchor.y + anchor.h + ROW_GAP), w: box.w, h: box.h };
    if (inside(r) && free(r)) return r;
  }
  // Candidate rows: the regular grid (height + 1.5 mm) plus "just below" every existing field;
  // candidate columns: 2.5 mm steps plus "just right of" every existing field. Scan down, then right.
  const stepY = Math.max(50, box.h + ROW_GAP);
  const ys = new Set();
  for (let y = MARGIN; y + box.h <= labelH - MARGIN; y += stepY) ys.add(y);
  for (const o of others) ys.add(Math.round(Math.max(MARGIN, o.y + o.h + ROW_GAP)));
  const xs = new Set([MARGIN]);
  if (!wide) {
    for (let x = MARGIN; x + box.w <= labelW - MARGIN; x += COL_STEP) xs.add(x);
    for (const o of others) xs.add(Math.round(Math.max(MARGIN, o.x + o.w + ROW_GAP)));
  }
  const rows = [...ys].filter((y) => y + box.h <= labelH - MARGIN).sort((a, b) => a - b);
  for (const x of [...xs].sort((a, b) => a - b)) {
    for (const y of rows) {
      const r = { x, y, w: box.w, h: box.h };
      if (inside(r) && free(r)) return r;
    }
  }
  return null;
}

/** Add a Text/Date/Barcode field and select it. init is passed to label.addField (e.g. { x, y, symbology }). */
export function addFieldOfKind(kind, init) {
  if (!S.label) {
    toast('Open or create a label first', 'warn');
    return null;
  }
  if (kind === 'barcode') ensureBwip().catch((e) => toast(e.message, 'warn'));
  const hasPos = init && Number.isFinite(init.x) && Number.isFinite(init.y);
  const others = S.fields.map((f) => fieldBounds(f));
  const sel = singleSelected();
  const anchor = sel ? fieldBounds(sel) : null;
  try {
    const key = mutate((label) => {
      let patch = { ...(init || {}) };
      if (hasPos) {
        patch.x = Math.round(Math.max(0, init.x));
        patch.y = Math.round(Math.max(0, init.y));
      } else {
        // Measure the new field with its template defaults, then place it for real (one history entry).
        const trialKey = addField(label, kind, patch);
        const box = localBox(getField(label, trialKey));
        removeField(label, trialKey);
        const slot = findFreeSlot(label.width, label.height, box, others, anchor);
        let x;
        let y;
        if (slot) {
          x = slot.x - box.x;
          y = slot.y - box.y;
        } else if (lastAdded) {
          x = lastAdded.x + 250;
          y = lastAdded.y + 250;
          if (x > label.width - MARGIN || y > label.height - MARGIN) {
            x = MARGIN;
            y = MARGIN;
          }
        } else {
          x = MARGIN;
          y = MARGIN;
        }
        patch = { ...patch, x: Math.round(Math.max(0, x)), y: Math.round(Math.max(0, y)) };
      }
      const k = addField(label, kind, patch);
      const f = getField(label, k);
      lastAdded = { x: f.x, y: f.y };
      return k;
    });
    toast(`Added ${KIND_NAMES[kind] || kind} field`, 'good');
    return key;
  } catch (err) {
    console.error(err);
    toast(`Couldn't add field: ${err.message}`, 'bad');
    return null;
  }
}

export function deleteSelected() {
  const list = selectedFields();
  if (!list.length) return;
  tryMutate((label) => {
    for (const f of list) removeField(label, f.key);
  });
  select([]);
  toast(list.length === 1 ? `Deleted ${list[0].name}` : `Deleted ${list.length} fields`);
}

export function duplicateSelected() {
  const list = selectedFields().filter((f) => f.kind !== 'unsupported');
  if (!list.length) return;
  tryMutate((label) => list.map((f) => duplicateField(label, f.key)));
}

export function bringToFront() {
  const list = selectedFields();
  if (!list.length) return;
  tryMutate((label) => {
    for (const f of list) moveField(label, f.key, S.fields.length - 1);
  });
}
export function sendToBack() {
  const list = selectedFields();
  if (!list.length) return;
  tryMutate((label) => {
    for (const f of [...list].reverse()) moveField(label, f.key, 0);
  });
}

export function toggleLockSelected() {
  const list = selectedFields().filter((f) => f.kind !== 'unsupported');
  if (!list.length) return;
  const lock = !list.every((f) => isLocked(f));
  for (const f of list) setLocked(f, lock);
  toast(lock ? 'Locked in the editor (not saved to the file)' : 'Unlocked');
}

export function selectAll() {
  select(S.fields.map((f) => f.key));
}

/** Rotate each editable selected field by +90° (the printer supports 0/90/180/270 only). */
export function rotateSelected(delta = 90) {
  const list = editableSelection();
  if (!list.length) return;
  tryMutate((label) => {
    for (const f of list) updateField(label, f.key, { rotation: ((((f.rotation || 0) + delta) % 360) + 360) % 360 });
  });
}

/** Move editable selection by (dx, dy) units, clamped to x,y >= 0. */
export function nudge(dx, dy) {
  const list = editableSelection();
  if (!list.length) return;
  tryMutate(
    (label) => {
      for (const f of list) updateField(label, f.key, { x: Math.max(0, Math.round(f.x + dx)), y: Math.max(0, Math.round(f.y + dy)) });
    },
    { tag: 'nudge' },
  );
}

function moveBy(label, f, dx, dy) {
  if (!dx && !dy) return;
  updateField(label, f.key, { x: Math.max(0, Math.round(f.x + dx)), y: Math.max(0, Math.round(f.y + dy)) });
}

/** Align selection to each other: align-l|align-cx|align-r|align-t|align-cy|align-b|dist-x|dist-y */
export function alignSelection(cmd) {
  const list = editableSelection();
  if (list.length < 2) return;
  const items = list.map((f) => ({ f, b: fieldBounds(f) }));
  const u = unionBounds(items.map((i) => i.b));
  tryMutate((label) => {
    if (cmd === 'dist-x' || cmd === 'dist-y') {
      if (items.length < 3) return;
      const horiz = cmd === 'dist-x';
      items.sort((a, b) => (horiz ? a.b.x - b.b.x : a.b.y - b.b.y));
      const total = items.reduce((s, i) => s + (horiz ? i.b.w : i.b.h), 0);
      const span = horiz ? u.w : u.h;
      const gap = (span - total) / (items.length - 1);
      let pos = horiz ? u.x : u.y;
      for (const i of items) {
        const cur = horiz ? i.b.x : i.b.y;
        moveBy(label, i.f, horiz ? pos - cur : 0, horiz ? 0 : pos - cur);
        pos += (horiz ? i.b.w : i.b.h) + gap;
      }
      return;
    }
    for (const { f, b } of items) {
      if (cmd === 'align-l') moveBy(label, f, u.x - b.x, 0);
      else if (cmd === 'align-r') moveBy(label, f, u.x + u.w - (b.x + b.w), 0);
      else if (cmd === 'align-cx') moveBy(label, f, u.x + u.w / 2 - (b.x + b.w / 2), 0);
      else if (cmd === 'align-t') moveBy(label, f, 0, u.y - b.y);
      else if (cmd === 'align-b') moveBy(label, f, 0, u.y + u.h - (b.y + b.h));
      else if (cmd === 'align-cy') moveBy(label, f, 0, u.y + u.h / 2 - (b.y + b.h / 2));
    }
  });
}

/** Align selection (as a group) to the label: left|cx|right|top|cy|bottom */
export function alignToLabel(cmd) {
  const list = editableSelection();
  if (!list.length || !S.label) return;
  const u = unionBounds(list.map((f) => fieldBounds(f)));
  const W = S.label.width;
  const H = S.label.height;
  let dx = 0;
  let dy = 0;
  if (cmd === 'left') dx = -u.x;
  else if (cmd === 'cx') dx = (W - u.w) / 2 - u.x;
  else if (cmd === 'right') dx = W - u.w - u.x;
  else if (cmd === 'top') dy = -u.y;
  else if (cmd === 'cy') dy = (H - u.h) / 2 - u.y;
  else if (cmd === 'bottom') dy = H - u.h - u.y;
  tryMutate((label) => {
    for (const f of list) moveBy(label, f, dx, dy);
  });
}

export const hasSelection = () => S.selection.length > 0;
export const canEditSelection = () => editableSelection().length > 0;
export { isLocked, emit };
